/**
 * A bounded wrapper around `renderer.compileAsync()`.
 *
 * WHY THIS EXISTS
 * ---------------
 * `compileAsync` does not resolve when the GPU finishes compiling — it POLLS
 * each program's `COMPLETION_STATUS_KHR` on a timer and resolves once they all
 * report ready. A driver that never reports a program ready therefore leaves
 * the promise **pending forever**: not rejected, pending. Every `await` of it
 * is written as `try { await compileAsync(...) } catch { compile(...) }`, and
 * that catch cannot fire on a hang, so boot simply stops.
 *
 * It fails silently and looks alive: no error, no frame, timers still running.
 * The AI hook in src/ai/index.js already carries a note measuring this same
 * polling at 26 s with every shader in the driver's disk cache, so "slow enough
 * to be indistinguishable from hung" is the documented normal case here.
 *
 * Pre-compiling shaders is an optimisation. Losing it costs a stutter on the
 * first frames that use a program. Waiting on it forever costs the session —
 * so every await of it is bounded, and a timeout is surfaced as a rejection so
 * the synchronous `renderer.compile()` fallback each call site already has
 * finally gets a chance to run.
 */

/** Ceiling for a single compileAsync call. */
export const COMPILE_TIMEOUT_MS = 8000;

/**
 * `compileAsync` with a deadline. Same arguments as the renderer method:
 *   await compileAsyncBounded(renderer, scene, camera)
 *   await compileAsyncBounded(renderer, scene, camera, targetScene)
 *
 * Rejects (rather than hanging) once COMPILE_TIMEOUT_MS has passed.
 */
/**
 * A driver that fails to report one program ready fails to report them all, and
 * boot awaits this from world, render, ai and the pre-warm. Paying the timeout
 * at every one of those turns an 8 s delay into a minute of dead screen, so the
 * first timeout latches and the rest fail fast into their sync fallback.
 */
let asyncCompileUnavailable = false;

export function compileAsyncBounded(renderer, ...args) {
  if (asyncCompileUnavailable) {
    return Promise.reject(new Error('compileAsync already timed out once on this driver'));
  }
  let timer;
  const deadline = new Promise((_resolve, reject) => {
    timer = setTimeout(() => {
      asyncCompileUnavailable = true;
      console.warn(`[shadercompile] compileAsync exceeded ${COMPILE_TIMEOUT_MS}ms — falling back to synchronous compiles for the rest of this session`);
      reject(new Error(`compileAsync exceeded ${COMPILE_TIMEOUT_MS}ms`));
    }, COMPILE_TIMEOUT_MS);
  });
  return Promise.race([renderer.compileAsync(...args), deadline]).finally(() => clearTimeout(timer));
}
